import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Alert, Badge, Button, Group, Loader, Modal, Select, Stack, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for MeetingAccessModal. Renders the sharing modal: loading
 * spinner, the organizer plus everyone the meeting is shared with (remove
 * icons on the latter), and a share-with picker over workspace members. The
 * smart wrapper at ../index.tsx owns data fetching and mutations.
 */
export const MeetingAccessModalView = ({
  opened,
  onClose,
  organizerEmail,
  sharedWithEmails,
  candidates,
  isLoading,
  errorMessage,
  candidatesError,
  isMutating,
  pendingEmail,
  selectedEmail,
  onSelectEmail,
  onAdd,
  onRemove,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()

  // Removing access is destructive (the person can no longer view the meeting),
  // so confirm first — mirrors the openConfirmModal pattern used for member
  // removal elsewhere.
  const confirmRemove = (email: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Remove access for ${email}?`),
      children: <Text size="sm">{i18n._(msg`They will no longer be able to view this meeting.`)}</Text>,
      labels: { confirm: i18n._(msg`Remove`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => onRemove(email),
    })
  }

  return (
    <Modal opened={opened} onClose={onClose} title={i18n._(msg`Share this meeting`)} size="md">
      {isLoading ? (
        <Group justify="center" py="lg">
          <Loader size="sm" />
        </Group>
      ) : errorMessage ? (
        <Alert color="danger" title={i18n._(msg`Could not load sharing settings`)}>
          {errorMessage}
        </Alert>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="sm">
            <Trans>Only the organizer and the people listed here can open this meeting.</Trans>
          </Text>
          <Stack gap="xs" mb="md">
            {[organizerEmail, ...sharedWithEmails.filter((e) => e.toLowerCase() !== organizerEmail.toLowerCase())].map(
              (email) => {
                const isOrganizer = email.toLowerCase() === organizerEmail.toLowerCase()
                return (
                  <Group key={email} justify="space-between">
                    <Group gap="xs">
                      <Text size="sm">{email}</Text>
                      {isOrganizer && (
                        <Badge size="xs" variant="light" color="success">
                          <Trans>organizer</Trans>
                        </Badge>
                      )}
                    </Group>
                    {!isOrganizer && (
                      <ActionIcon
                        variant="subtle"
                        color="danger"
                        size="sm"
                        aria-label={i18n._(msg`Remove ${email}`)}
                        loading={isMutating && pendingEmail === email}
                        onClick={() => confirmRemove(email)}
                      >
                        ✕
                      </ActionIcon>
                    )}
                  </Group>
                )
              },
            )}
            {sharedWithEmails.length === 0 && (
              <Text size="sm" c="dimmed">
                <Trans>Not shared with anyone else yet.</Trans>
              </Text>
            )}
          </Stack>
          {candidatesError && (
            <Alert color="danger" mb="sm" title={i18n._(msg`Could not load workspace members`)}>
              {candidatesError}
            </Alert>
          )}
          <Group gap="sm">
            <Select
              disabled={!!candidatesError}
              placeholder={i18n._(msg`Search workspace members…`)}
              searchable
              clearable
              data={candidates.map((c) => ({
                value: c.email,
                label: `${c.name} <${c.email}>`,
              }))}
              value={selectedEmail}
              onChange={onSelectEmail}
              style={{ flex: 1 }}
            />
            <Button
              disabled={!selectedEmail}
              loading={isMutating && pendingEmail === selectedEmail}
              onClick={() => {
                if (!selectedEmail) return
                onAdd(selectedEmail)
              }}
            >
              <Trans>Share</Trans>
            </Button>
          </Group>
        </>
      )}
    </Modal>
  )
}
